import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { getDailyPractice } from "@/lib/academy.functions";
import { AcademyNav } from "@/components/academy/AcademyNav";
import { Sunrise } from "lucide-react";
import { ACADEMY_ID, breadcrumbs, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Daily Practice — Buddha KA Academy";
const DESCRIPTION =
  "One lesson, one reflection prompt, and one micro-practice drawn from the Buddha KA Academy canon — renewed every day.";

export const Route = createFileRoute("/academy/daily")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/academy/daily" }),
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: "https://buddhaka.com/academy/daily#daily",
          url: "https://buddhaka.com/academy/daily",
          name: TITLE,
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
          extra: { isPartOf: { "@id": ACADEMY_ID } },
        }),
      ),
      ldScript(breadcrumbs([["Academy", "/academy"], ["Daily Practice", "/academy/daily"]])),
    ],
  }),
  component: DailyPage,
});


function DailyPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["academy", "daily"],
    queryFn: () => getDailyPractice({ data: {} }),
  });

  const lesson = data?.lesson;
  const prompts = (lesson?.prompts as { reflection?: string[]; practice?: string } | null) ?? null;

  return (
    <>
      <AcademyNav />
      <div className="min-h-dvh bg-obsidian text-foreground">
        <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
          <p className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><Sunrise size={14} /> Today&rsquo;s Practice</p>
          {/* Always render a single H1 so the page has a stable document
              outline before the client query resolves. */}
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-gold-luminous">
            {lesson?.title ?? "Daily Practice"}
          </h1>
          {isLoading && <p className="mt-6 text-sm text-foreground/60">Loading…</p>}
          {!isLoading && !lesson && <p className="mt-6 text-sm text-foreground/60">No practice available yet.</p>}
          {lesson && (
            <>
              {lesson.subtitle && <p className="mt-2 text-lg text-foreground/70">{lesson.subtitle}</p>}
              {lesson.summary && <p className="mt-6 text-base leading-relaxed text-foreground/80">{lesson.summary}</p>}

              {prompts?.reflection?.length ? (
                <section className="mt-8 rounded-2xl border hairline p-5">
                  <h2 className="text-xs uppercase tracking-widest text-gold">Reflect</h2>
                  <ul className="mt-3 space-y-2 text-sm">
                    {prompts.reflection.map((r, i) => (
                      <li key={i} className="text-foreground/80">• {r}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {prompts?.practice ? (
                <section className="mt-4 rounded-2xl border hairline p-5">
                  <h2 className="text-xs uppercase tracking-widest text-gold">Practice</h2>
                  <p className="mt-3 text-sm text-foreground/80">{prompts.practice}</p>
                </section>
              ) : null}

              <div className="mt-8 flex flex-wrap gap-3">
                {data?.domain && (
                  <Link to="/academy/$domain/lesson/$lesson" params={{ domain: data.domain.slug, lesson: lesson.slug }}
                    className="inline-flex items-center rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
                    Open full lesson
                  </Link>
                )}
                <Link to="/academy/journal"
                  className="inline-flex items-center rounded-full border hairline px-5 py-2.5 text-sm">
                  Journal on this
                </Link>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}
